import type { Status } from "@/types";

export interface KanbanColumn {
  id: string;
  statusId: string | null;
  label: string;
  color: string;
  hidden: boolean;
  order: number;
}

export const KANBAN_UNMAPPED_ID = "__unmapped__";

/** Generate a stable unique id for a Kanban column. */
export function genColumnId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `col_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }
}

/**
 * Resolve the effective ordered column list for a Kanban view.
 *
 * Every column maps to exactly one existing Status. When a saved column config is passed it is
 * treated as authoritative — columns are used exactly as saved (relabelled / recoloured /
 * reordered / hidden / added / removed), except any column whose status no longer exists is
 * dropped. Intentionally removed columns stay removed and newly created statuses flow into the
 * read-only "No column" bucket until the owner adds a column.
 *
 * With no saved config the columns are derived directly from the Statuses list ordered by
 * `displayOrder` — one column per status. This is the out-of-box default.
 */
export function buildKanbanColumns(statuses: Status[], saved?: KanbanColumn[]): KanbanColumn[] {
  const validStatusIds = new Set(statuses.map((s) => s.id));

  if (saved && saved.length > 0) {
    return saved
      .slice()
      .sort((a, b) => a.order - b.order)
      .filter((col) => col.statusId && validStatusIds.has(col.statusId)) // drop unmapped / deleted-status columns
      .map((col, i) => ({
        id: col.id ?? col.statusId ?? genColumnId(),
        statusId: col.statusId,
        label: col.label,
        color: col.color,
        hidden: !!col.hidden,
        order: i,
      }));
  }

  return statuses
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((st, i) => ({
      id: st.id,
      statusId: st.id,
      label: st.name,
      color: st.color,
      hidden: false,
      order: i,
    }));
}
